import { useEffect, useMemo, useState } from 'react'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import {
  newRacePlan,
  parseRacePlan,
  planCovers,
  toStoredRacePlan,
  type RacePlanDoc,
  type SessionDoc,
} from '@racedash/shared'
import { db } from '../lib/firebase'

export interface StoredRacePlan extends RacePlanDoc {
  id: string
}

/**
 * A team's plans for one event.
 *
 * Keyed by an id of their own rather than by a catalogue session, because a
 * plan cannot depend on a timetable that usually does not exist yet. A weekend
 * with two races is two plans, each carrying its own start and end, and the
 * timetable — when there is one — only pre-fills them.
 */
export function useRacePlans(orgId: string | undefined, eventId: string | undefined) {
  const [plans, setPlans] = useState<StoredRacePlan[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId || !eventId) {
      setPlans(null)
      return
    }
    return onSnapshot(
      query(collection(db, 'orgs', orgId, 'racePlans'), where('eventId', '==', eventId)),
      (snap) =>
        setPlans(
          snap.docs
            .map((d) => ({ id: d.id, ...parseRacePlan(d.data()) }))
            .sort((a, b) => (a.startsAt ?? 0) - (b.startsAt ?? 0)),
        ),
      (e) => {
        // Reported rather than folded into "no plans". A plan that cannot be
        // read and one that was never made look identical otherwise, and only
        // one of them means somebody should go and look.
        setError(e.message)
        setPlans([])
      },
    )
  }, [orgId, eventId])

  return { plans, error }
}

export async function createRacePlan(
  orgId: string,
  eventId: string,
  name: string,
): Promise<string> {
  const ref = await addDoc(collection(db, 'orgs', orgId, 'racePlans'), {
    ...toStoredRacePlan(newRacePlan(eventId, name)),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function saveRacePlan(
  orgId: string,
  planId: string,
  plan: RacePlanDoc,
): Promise<void> {
  await setDoc(doc(db, 'orgs', orgId, 'racePlans', planId), {
    ...toStoredRacePlan(plan),
    updatedAt: serverTimestamp(),
  })
}

export async function deleteRacePlan(orgId: string, planId: string): Promise<void> {
  await deleteDoc(doc(db, 'orgs', orgId, 'racePlans', planId))
}

export interface CatalogueSession extends SessionDoc {
  id: string
}

/**
 * An event's timetable, where it has one.
 *
 * World-readable catalogue data, and entirely optional: it pre-fills a plan's
 * start and end and gives it a name, and its absence changes nothing except
 * that those are typed instead.
 */
export function useSessions(eventId: string | undefined) {
  const [sessions, setSessions] = useState<CatalogueSession[] | null>(null)

  useEffect(() => {
    if (!eventId) {
      setSessions(null)
      return
    }
    return onSnapshot(
      query(collection(db, 'sessions'), where('eventId', '==', eventId)),
      (snap) =>
        setSessions(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as SessionDoc) }))
            .sort(
              (a, b) => (a.scheduledStart?.toMillis() ?? 0) - (b.scheduledStart?.toMillis() ?? 0),
            ),
        ),
      () => setSessions([]),
    )
  }, [eventId])

  return sessions
}

/**
 * The plan that applies to what is on screen right now.
 *
 * Resolved against each plan's own start and end, which sidesteps a problem the
 * pipeline deliberately left open: a timing bucket carries no session id,
 * because deciding which configured session a stream belongs to is a judgement
 * nobody has made. Plans do not overlap, so "the one covering now" is an
 * unambiguous answer to a question that never has to be asked globally.
 *
 * Falls back to the most recent plan to have started, so a plan stays on screen
 * after the flag rather than vanishing at it.
 */
export function useActivePlan(
  orgId: string | undefined,
  eventId: string | undefined,
  at: Date,
) {
  const { plans } = useRacePlans(orgId, eventId)

  // Rounded to the minute: `at` ticks every few seconds and which plan applies
  // changes when a race begins or ends, not continuously.
  const minute = Math.floor(at.getTime() / 60_000)

  return useMemo(() => {
    if (!plans?.length) return null
    const when = new Date(minute * 60_000)
    const covering = plans.find((p) => planCovers(p, when))
    if (covering) return covering

    // Outside any race, the useful plan is the nearest one in time. Backwards
    // first, because immediately after a race the thing being looked at is the
    // race that just finished. Then forwards, because on the Saturday of a
    // Sunday race there is nothing behind and the plan being reviewed is
    // obviously the one about to be run — and a planner screen that is blank
    // until the flag drops is blank for exactly the days it would be used.
    const dated = plans.filter((p) => p.startsAt != null)
    const started = dated.filter((p) => (p.startsAt as number) <= when.getTime())
    if (started.length) return started.at(-1) ?? null
    const upcoming = dated.filter((p) => (p.startsAt as number) > when.getTime())
    return upcoming[0] ?? null
  }, [plans, minute])
}
